import { STOCK_UI } from '@diamond/shared/decimal-util-web';
import { BackofficeActivitiesOrderItem } from '@diamond/shared/types';
import { dinero, toDecimal } from 'dinero.js';
import { useMemo } from 'react';

/**
 * Resolves quantity value with clear priority:
 * 1) explicit quantity param
 * 2) quantity_free_goods when > 0
 * 3) confirmed_quantity, then quantity when present
 * 4) fallback to 0
 */
const getRawQuantity = (
  item: BackofficeActivitiesOrderItem,
  quantity?: number
): number => {
  if (quantity !== undefined) {
    return quantity;
  }

  const freeGoodsQuantity = Number(item.quantity_free_goods);
  if (Number.isFinite(freeGoodsQuantity) && freeGoodsQuantity > 0) {
    return freeGoodsQuantity;
  }

  const regularQuantity = Number(item.confirmed_quantity ?? item.quantity);
  if (Number.isFinite(regularQuantity)) {
    return regularQuantity;
  }

  return 0;
};

export function useQuantityFormat(
  item: BackofficeActivitiesOrderItem,
  quantity?: number
) {
  const sanitizeAmount = (value: number): number => {
    if (!Number.isFinite(value)) {
      return 0;
    }
    return Math.trunc(value);
  };

  const formattedQuantity = useMemo(() => {
    const rawQuantity = getRawQuantity(item, quantity);
    const quantityToFormat = sanitizeAmount(rawQuantity);

    return Number(
      toDecimal(
        dinero({
          amount: quantityToFormat,
          currency: STOCK_UI,
        })
      )
    );
  }, [item, quantity]);

  const formattedMultiplier = useMemo(() => {
    const multiplierRaw =
      item.order_quantity_multiple !== undefined
        ? Number(item.order_quantity_multiple)
        : 0;
    const multiplier = sanitizeAmount(multiplierRaw);
    const demagnifiedMultiplier = Number(
      toDecimal(
        dinero({
          amount: multiplier,
          currency: STOCK_UI,
        })
      )
    );

    if (demagnifiedMultiplier > 0 && demagnifiedMultiplier !== 1) {
      return demagnifiedMultiplier;
    }

    return null;
  }, [item.order_quantity_multiple]);

  const displayWithMultiplier = useMemo(() => {
    if (formattedMultiplier) {
      return {
        quantity: formattedQuantity,
        multiplier: formattedMultiplier,
      };
    }

    return {
      quantity: formattedQuantity,
      multiplier: null,
    };
  }, [formattedQuantity, formattedMultiplier]);

  return {
    quantity: formattedQuantity,
    multiplier: formattedMultiplier,
    display: displayWithMultiplier,
  };
}
